import { AnimatedNumber, Skeleton } from '~/components/component-library'
import {
  buildHiddenText,
  buildPrefix,
  buildValue,
  getFormatter,
} from '~/components/component-library/CurrencyAmount/helpers'
import { currencyAmountVariants } from '~/components/component-library/CurrencyAmount/styles'
import { CurrencyAmountProps } from '~/components/component-library/CurrencyAmount/types'
import { usePrivacyMode } from '~/hooks/usePrivacyMode'
import { cn } from '~/utils/tailwind-utils'

export { type CurrencyAmountProps } from '~/components/component-library/CurrencyAmount/types'

export const CurrencyAmount = (props: CurrencyAmountProps) => {
  const { value, isLoading = false } = props
  const isPrivacyModeOn = usePrivacyMode()

  // Skeleton bar is sized to glyph mass (≈ font-size), not full line-height,
  // so loaded text doesn't visibly settle on hydration.
  if (isLoading || value === undefined) {
    if (props.layout === 'inline') {
      return (
        <Skeleton
          className="inline-block h-[1em] flex-shrink-0 align-middle"
          length={2}
        />
      )
    }
    const size = props.size ?? 'base'
    return (
      <span
        className={cn('inline-flex items-center justify-center', {
          'h-5': size === 'small',
          'h-6': size === 'base',
        })}
      >
        <Skeleton
          className={cn({
            'h-3': size === 'small',
            'h-4': size === 'base',
          })}
          length={2}
        />
      </span>
    )
  }

  if (isPrivacyModeOn) {
    const direction = props.layout === 'inline' ? 'neutral' : props.direction
    const hiddenText = buildHiddenText(
      direction ?? 'neutral',
      props.currency ?? 'USD',
    )
    if (props.layout === 'inline') {
      return <span className="tabular-nums">{hiddenText}</span>
    }
    return (
      <span
        className={cn(currencyAmountVariants({ size: props.size, direction }))}
      >
        {hiddenText}
      </span>
    )
  }

  // Static render — NumberFlow's width transitions shift surrounding layout.
  if (props.layout === 'inline') {
    return (
      <span className="tabular-nums">
        {buildPrefix('neutral', value)}
        {getFormatter(props.currency ?? 'USD').format(buildValue(value))}
      </span>
    )
  }

  const direction = props.direction ?? 'neutral'
  const size = props.size ?? 'base'

  return (
    <AnimatedNumber
      className={cn(currencyAmountVariants({ size, direction }))}
      prefix={buildPrefix(direction, value)}
      format={{
        style: 'currency',
        currency: props.currency ?? 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }}
      value={buildValue(value)}
    />
  )
}
